import { useCallback } from 'react'
import { UseData } from './useData'
import { servicesToPOSTModel } from '../mappers'
import {
  ILeadOppProduct,
  reorderLeadOppProduct,
  updateOppName,
} from '@user/store/leadOppProducts'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { Props as ReOrderServicesFormProps } from '../ReOrderServicesForm'
import {
  IOpportunity,
  selectOpportunityById,
  selectOpportunityDetail,
  updateName,
} from '@user/store/opportunity'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts/slice'
import { useAppSelector } from '@user/store/hooks'
import { DropResult } from '@user/utils/Draggable'

type Props = Pick<
  UseData,
  | 'servicesReOrderTemp'
  | 'setServicesReOrderTemp'
  | 'setDisableCancel'
  | 'setDisableSave'
> &
  Pick<ReOrderServicesFormProps, 'entityType' | 'onClose'>

export function useHandlers(props: Props) {
  const {
    entityType,
    onClose,
    servicesReOrderTemp,
    setServicesReOrderTemp,
    setDisableCancel,
    setDisableSave,
  } = props

  const dispatch = useAppDispatch()

  const currentServices: ILeadOppProduct[] = useAppSelector((state) =>
    selectAllLeadOppProducts(state),
  )

  const entityId = currentServices.length > 0 ? currentServices[0].entityId : -1
  const opportunity: IOpportunity | undefined = useAppSelector((state) =>
    selectOpportunityById(state, entityId!),
  )

  const opportunityDetail = useAppSelector((state) =>
    selectOpportunityDetail(state),
  )

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const dropped = servicesReOrderTemp[result.sourceIndex]
      const newServices = [...servicesReOrderTemp]

      newServices.splice(result.sourceIndex, 1)
      newServices.splice(result.destinationIndex, 0, dropped)

      setServicesReOrderTemp(newServices)
    },
    [servicesReOrderTemp, setServicesReOrderTemp],
  )

  const onSave = useCallback(async () => {
    setDisableCancel(true)
    setDisableSave(true)
    const input = servicesToPOSTModel(servicesReOrderTemp, entityType)
    const result = await dispatch(reorderLeadOppProduct(input))
    if (reorderLeadOppProduct.fulfilled.match(result)) {
      if (
        (opportunity && opportunity.useSystemName) ||
        (opportunityDetail && opportunityDetail.useSystemName && entityId)
      ) {
        const updatedName = updateOppName(
          entityId!,
          input,
          opportunityDetail,
          opportunity,
          currentServices,
        )
        if (updatedName) await dispatch(updateName(updatedName))
      }
    }
    setDisableCancel(false)
    setDisableSave(false)
    onClose()
  }, [
    dispatch,
    entityType,
    onClose,
    opportunity,
    opportunityDetail,
    servicesReOrderTemp,
    setDisableCancel,
    setDisableSave,
    currentServices,
    entityId,
  ])

  return { handleDragEnd, onSave }
}
